import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import FolderOffRoundedIcon from '@mui/icons-material/FolderOffRounded'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import PageHeader from 'components/atoms/PageHeader'
import BpList from 'features/bpList/BpList'
import UnitsList from 'features/bpList/UnitsList'
import { font, ink, line, radius, shadow, surface, transition } from 'features/dashboard/tokens'
import { scoreColour, formatPercent } from 'features/dashboard/charts/scoreColour'
import { selectHasPortfoliosAccess, selectComplianceEnabled, selectPortfolioZoomLevel } from 'features/auth/ducks/selectors'
import { getAltitudeSummary, createPortfolio as createPortfolioApi } from 'services/portfolioService'
import { getJumpTargets } from 'services/BpService'
import { PRIVATE_PATHS } from 'utils/paths'
import LevelTabs from './components/LevelTabs'
import AttentionBand from './components/AttentionBand'
import FocusBreadcrumb from './components/FocusBreadcrumb'
import EntityGrid from './components/EntityGrid'
import PortfolioContents from './components/PortfolioContents'
import PassportsMap from './components/PassportsMap'
import CreatePortfolioModal from 'features/portfolio/components/CreatePortfolioModal'
import EstateEditorModal from 'features/bpList/Components/estates/EstateEditorModal'
import { updateUserRoutine } from 'features/auth/ducks/actions'

const ALTITUDES = ['portfolio', 'estate', 'bp', 'unit']

const PortfoliosArea = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const hasPortfoliosAccess = useSelector(selectHasPortfoliosAccess)

  const estateId = params.estateId || null
  const portfolioParam = searchParams.get('portfolio')
  const levelParam = searchParams.get('level')
  const portfolioId = estateId ? params.portfolioId : (portfolioParam === 'ungrouped' ? null : portfolioParam)

  const focus = useMemo(() => {
    if (estateId) return { type: 'estate', id: estateId }
    if (portfolioId) return { type: 'portfolio', id: portfolioId }
    if (portfolioParam === 'ungrouped') return { type: 'ungrouped' }
    return { type: 'all' }
  }, [estateId, portfolioId, portfolioParam])

  const savedZoom = useSelector(selectPortfolioZoomLevel)

  const altitude = useMemo(() => {
    if (focus.type === 'estate') return 'estate'
    if (focus.type === 'portfolio' || focus.type === 'ungrouped') return 'portfolio'
    if (ALTITUDES.includes(levelParam)) return levelParam
    // Remember the user's last-used level; default to buildings for a fresh user.
    return ALTITUDES.includes(savedZoom) ? savedZoom : 'bp'
  }, [focus.type, levelParam, savedZoom])

  const [summary, setSummary] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateEstate, setShowCreateEstate] = useState(false)
  const [reload, setReload] = useState(0)
  const [query, setQuery] = useState('')
  const dispatch = useDispatch()
  const showCompliance = useSelector(selectComplianceEnabled)
  const requestRef = useRef(0)

  useEffect(() => {
    const query = {}
    if (focus.type === 'portfolio') query.portfolio = focus.id
    if (focus.type === 'ungrouped') query.portfolio = 'ungrouped'
    if (focus.type === 'estate') query.estate = focus.id
    const token = ++requestRef.current
    setLoading(true)
    getAltitudeSummary(query)
      .then(res => {
        if (token !== requestRef.current) return
        setSummary(res.data?.data || null)
        setLoading(false)
      })
      .catch(() => {
        if (token !== requestRef.current) return
        setSummary(null)
        setLoading(false)
      })
  }, [focus.type, focus.id, reload])

  const counts = useMemo(() => {
    const base = summary?.counts || { portfolios: 0, estates: 0, bps: 0 }
    return {
      portfolio: base.portfolios ?? 0,
      estate: base.estates ?? 0,
      bp: base.bps ?? 0,
      unit: base.units ?? 0,
    }
  }, [summary])

  const singleEntityRef = useRef(false)
  useEffect(() => {
    if (focus.type !== 'all' || levelParam || portfolioParam) return
    if (!summary || singleEntityRef.current) return
    // Only auto-focus a lone building/unit when there is genuinely nothing else
    // to see — a user who also has portfolios or estates may have come here
    // deliberately.
    if ((summary.portfolios?.length || 0) > 0 || (summary.estates?.length || 0) > 0) return
    const onlyOneBp = counts.bp === 1
    const onlyOneUnit = counts.bp === 0 && counts.unit === 1
    if (!onlyOneBp && !onlyOneUnit) return
    singleEntityRef.current = true
    getJumpTargets()
      .then(res => {
        const items = res.data?.data || []
        if (onlyOneBp) {
          const bp = items.find(t => t.type === 'bp')
          if (bp) navigate(`/bp-list/${bp.id}`, { replace: true })
        } else {
          const unit = items.find(t => t.type === 'unit')
          if (unit?.parentId) navigate(`/bp-list/${unit.parentId}/units/${unit.id}`, { replace: true })
        }
      })
      .catch(() => {})
  }, [summary, counts, focus.type, levelParam, portfolioParam, navigate])

  const portfolios = summary?.portfolios || []
  const estates = summary?.estates || []
  const mapPoints = summary?.mapPoints || []
  const ungroupedBpCount = summary?.ungroupedBpCount ?? 0
  const focusBuildingPassports = summary?.focusBuildingPassports || []
  const looseBps = useMemo(() => focusBuildingPassports.map(b => ({
    id: b.id,
    name: b.name,
    estateId: b.estateId ?? null,
    uprn: b.uprn ?? null,
    city: b.city ?? null,
    postalCode: b.postalCode ?? null,
    status: b.status ?? null,
    buildingOwner: b.buildingOwner ?? null,
    createdAt: b.createdAt ?? null,
    score: b.score ?? null,
    unmetStatutory: b.unmetStatutory || 0,
  })), [focusBuildingPassports])

  const focusEstates = useMemo(() => (summary?.focusEstates || []).map(e => ({
    id: e.id,
    name: e.name,
    score: e.score ?? null,
    unmetStatutory: e.unmetStatutory || 0,
    bpCount: e.bpCount ?? 0,
  })), [summary])

  const focusedPortfolio = useMemo(
    () => (focus.type === 'portfolio' || focus.type === 'estate'
      ? portfolios.find(p => String(p.id) === String(portfolioId)) || null
      : null),
    [focus.type, portfolios, portfolioId]
  )
  const focusedEstate = useMemo(
    () => (focus.type === 'estate'
      ? estates.find(e => String(e.id) === String(estateId)) || null
      : null),
    [focus.type, estates, estateId]
  )

  const trail = useMemo(() => {
    const crumbs = [{ key: 'all', label: 'All' }]
    if (focus.type === 'ungrouped') {
      crumbs.push({ key: 'ungrouped', label: 'Ungrouped' })
    }
    if (focusedPortfolio) {
      crumbs.push({ key: 'portfolio', id: focusedPortfolio.id, label: focusedPortfolio.name })
    } else if ((focus.type === 'portfolio' || focus.type === 'estate') && portfolioId) {
      crumbs.push({ key: 'portfolio', id: portfolioId, label: 'Portfolio' })
    }
    if (focusedEstate) {
      crumbs.push({ key: 'estate', id: focusedEstate.id, label: focusedEstate.name })
    } else if (focus.type === 'estate' && estateId) {
      crumbs.push({ key: 'estate', id: estateId, label: 'Estate' })
    }
    return crumbs
  }, [focus.type, focusedPortfolio, focusedEstate, portfolioId, estateId])

  const goAll = useCallback(level => {
    const qs = level && level !== 'portfolio' ? `?level=${level}` : ''
    navigate(`${PRIVATE_PATHS.portfolios}${qs}`)
  }, [navigate])

  const goPortfolio = useCallback((id, level) => {
    const parts = [`portfolio=${id}`]
    if (level && level !== 'estate') parts.push(`level=${level}`)
    navigate(`${PRIVATE_PATHS.portfolios}?${parts.join('&')}`)
  }, [navigate])

  const goEstate = useCallback((pid, eid) => {
    navigate(`/estates/${eid}`)
  }, [navigate])

  const goUngrouped = useCallback(() => {
    navigate(`${PRIVATE_PATHS.portfolios}?portfolio=ungrouped`)
  }, [navigate])

  const attentionItems = useMemo(() => {
    if (!showCompliance) return []
    const out = []
    portfolios.forEach(p => {
      const unmet = p.compliance?.unmetStatutory || 0
      if (unmet > 0) out.push({
        key: `p-${p.id}`,
        typeLabel: 'Portfolio',
        name: p.name,
        unmet,
        score: p.compliance?.score ?? null,
        meta: `${p.compliance?.estateCount ?? 0} ${p.compliance?.estateCount === 1 ? 'estate' : 'estates'} · ${p.compliance?.bpCount ?? 0} Building Passports`,
        onOpen: () => navigate(`/portfolios/${p.id}`),
        onReview: () => navigate(`/portfolios/${p.id}`),
      })
    })
    estates.forEach(e => {
      if (e.portfolioId) return
      const unmet = e.compliance?.unmetStatutory || 0
      if (unmet > 0) out.push({
        key: `e-${e.id}`,
        typeLabel: 'Estate',
        name: e.name,
        unmet,
        score: e.compliance?.score ?? null,
        meta: `${e.compliance?.buildings?.length ?? 0} Building Passports`,
        onOpen: () => goEstate(e.portfolioId, e.id),
        onReview: () => goEstate(e.portfolioId, e.id),
      })
    })
    return out.sort((a, b) => b.unmet - a.unmet).slice(0, 8)
  }, [portfolios, estates, showCompliance, navigate, goEstate])

  const handleAltitude = useCallback(key => {
    if (ALTITUDES.includes(key) && key !== savedZoom) {
      dispatch(updateUserRoutine({ portfolioZoomLevel: key, __silent: true }))
    }
    goAll(key)
  }, [goAll, dispatch, savedZoom])

  const handleCrumb = useCallback(index => {
    const crumb = trail[index]
    if (!crumb || crumb.key === 'all') return goAll('portfolio')
    if (crumb.key === 'ungrouped') return goUngrouped()
    if (crumb.key === 'portfolio') return goPortfolio(crumb.id)
    if (crumb.key === 'estate') return goEstate(portfolioId, crumb.id)
  }, [trail, goAll, goUngrouped, goPortfolio, goEstate, portfolioId])

  const handleCreatePortfolio = useCallback(async values => {
    try {
      const res = await createPortfolioApi(values)
      const created = res.data.data
      toast.success('Portfolio created')
      setShowCreate(false)
      goPortfolio(created.id)
    } catch (e) {
      toast.error('Failed to create portfolio')
    }
  }, [goPortfolio])

  const portfolioItems = useMemo(() => {
    const cards = portfolios.map((p, index) => ({
      key: `portfolio-${p.id}`,
      variant: 'portfolio',
      ...(index === 0 ? { dataTourId: 'portfolios.first-card' } : {}),
      name: p.name,
      score: p.compliance?.score ?? null,
      unmetStatutory: p.compliance?.unmetStatutory || 0,
      unitCount: p.compliance?.bpCount ?? 0,
      stats: [
        [p.compliance?.estateCount ?? 0, (p.compliance?.estateCount === 1 ? 'estate' : 'estates')],
        [p.compliance?.bpCount ?? 0, 'Building Passports'],
      ],
      onOpen: () => navigate(`/portfolios/${p.id}`),
    }))
    if (ungroupedBpCount > 0) {
      cards.push({
        key: 'ungrouped',
        variant: 'ungrouped',
        name: 'Ungrouped',
        score: null,
        unmetStatutory: 0,
        unitCount: ungroupedBpCount,
        stats: [[ungroupedBpCount, 'Building Passports']],
        onOpen: goUngrouped,
      })
    }
    return cards
  }, [portfolios, ungroupedBpCount, navigate, goUngrouped])

  const estateItems = useMemo(() => {
    const scoped = focus.type === 'portfolio'
      ? estates.filter(e => String(e.portfolioId) === String(portfolioId))
      : estates
    return scoped.map(e => ({
      key: `estate-${e.id}`,
      variant: 'estate',
      name: e.name,
      score: e.compliance?.score ?? null,
      unmetStatutory: e.compliance?.unmetStatutory || 0,
      unitCount: e.compliance?.buildings?.length ?? 0,
      stats: [[e.compliance?.buildings?.length ?? 0, 'Building Passports']],
      onOpen: () => goEstate(e.portfolioId || portfolioId, e.id),
    }))
  }, [estates, focus.type, portfolioId, goEstate])

  const estateBpItems = useMemo(() =>
    (focusedEstate?.compliance?.buildings || []).map(b => ({
      key: `bp-${b.id}`,
      variant: 'bp',
      name: b.name,
      score: b.score ?? null,
      unmetStatutory: b.unmetStatutory || 0,
      onOpen: () => navigate(`/bp-list/${b.id}`),
    })),
    [focusedEstate, navigate])

  const focusedContainer = focusedEstate
    ? {
        name: focusedEstate.name,
        score: focusedEstate.compliance?.score ?? null,
        childSummary: `${(focusedEstate.compliance?.buildings || []).length} Building Passports`,
        detailPath: PRIVATE_PATHS.estateDetail.replace(':id', estateId),
      }
    : focusedPortfolio
      ? {
          name: focusedPortfolio.name,
          score: focusedPortfolio.compliance?.score ?? null,
          childSummary: `${focusedPortfolio.compliance?.estateCount ?? focusEstates.length} estates · ${focusedPortfolio.compliance?.bpCount ?? 0} Building Passports`,
          detailPath: PRIVATE_PATHS.portfolioDetail.replace(':id', focusedPortfolio.id),
        }
      : focus.type === 'ungrouped'
        ? {
            name: 'Ungrouped Building Passports',
            score: null,
            childSummary: `${looseBps.length} Building Passports · not in a portfolio`,
            detailPath: null,
          }
        : null

  const isTop = focus.type === 'all'
  const q = query.trim().toLowerCase()
  const visiblePortfolioItems = useMemo(
    () => (q ? portfolioItems.filter(i => (i.name || '').toLowerCase().includes(q)) : portfolioItems),
    [portfolioItems, q]
  )
  const visibleEstateItems = useMemo(
    () => (q ? estateItems.filter(i => (i.name || '').toLowerCase().includes(q)) : estateItems),
    [estateItems, q]
  )
  const showSearch = isTop && (altitude === 'portfolio' || altitude === 'estate')
  const browseMeta = useMemo(() => {
    if (altitude === 'portfolio') return { shown: visiblePortfolioItems.length, total: portfolioItems.length, noun: 'portfolios', filterable: true }
    if (altitude === 'estate') return { shown: visibleEstateItems.length, total: estateItems.length, noun: 'estates', filterable: true }
    if (altitude === 'bp') return { shown: counts.bp, total: counts.bp, noun: counts.bp === 1 ? 'Building Passport' : 'Building Passports', filterable: false }
    if (altitude === 'unit') return { shown: counts.unit, total: counts.unit, noun: counts.unit === 1 ? 'Unit Passport' : 'Unit Passports', filterable: false }
    return null
  }, [altitude, visiblePortfolioItems, portfolioItems, visibleEstateItems, estateItems, counts])
  const showingText = browseMeta
    ? (browseMeta.filterable && q
        ? `Showing ${browseMeta.shown} of ${browseMeta.total} ${browseMeta.noun}`
        : `${browseMeta.total.toLocaleString()} ${browseMeta.noun}`)
    : ''

  const renderGrid = () => {
    if (focus.type === 'portfolio') {
      if (summary && !focusedPortfolio) {
        return (
          <Unavailable role='status'>
            <UnavailableIcon aria-hidden='true'><FolderOffRoundedIcon style={{ fontSize: 26 }} /></UnavailableIcon>
            <UnavailableTitle>This portfolio isn’t available</UnavailableTitle>
            <UnavailableText>
              It may have been removed, or it belongs to another account and hasn’t been shared with you. Check the web address, or ask whoever manages it to share it with you.
            </UnavailableText>
            <UnavailableBtn type='button' onClick={() => goAll('portfolio')}>
              Back to all portfolios
            </UnavailableBtn>
          </Unavailable>
        )
      }
      return (
        <PortfolioContents
          portfolioId={portfolioId}
          estates={focusEstates}
          buildings={looseBps}
          portfolios={portfolios}
          onOpenBp={id => navigate(`/bp-list/${id}`)}
          onDrillEstate={eid => goEstate(portfolioId, eid)}
          onCreated={() => setReload(r => r + 1)}
        />
      )
    }
    if (focus.type === 'estate') {
      return <EntityGrid items={estateBpItems} emptyText='This estate has no Building Passports yet.' showCompliance={showCompliance} />
    }
    if (focus.type === 'ungrouped') {
      return (
        <PortfolioContents
          portfolioId='ungrouped'
          estates={[]}
          buildings={looseBps}
          portfolios={portfolios}
          onOpenBp={id => navigate(`/bp-list/${id}`)}
          onDrillEstate={() => {}}
          onCreated={() => setReload(r => r + 1)}
        />
      )
    }
    if (altitude === 'portfolio') {
      return (
        <EntityGrid
          items={visiblePortfolioItems}
          canCreate={hasPortfoliosAccess && !q}
          createLabel='New portfolio'
          onCreate={() => setShowCreate(true)}
          emptyText={q
            ? `No portfolios match “${query.trim()}”.`
            : counts.bp === 0
              ? 'No Building Passports yet — create your first one with the button above to get started.'
              : 'No portfolios yet — group your Building Passports into one.'}
          showCompliance={showCompliance}
        />
      )
    }
    if (altitude === 'estate') {
      return <EntityGrid items={visibleEstateItems} emptyText={q ? `No estates match “${query.trim()}”.` : 'No estates yet.'} showCompliance={showCompliance} />
    }
    if (altitude === 'unit') {
      return <UnitsList />
    }
    return <BpList />
  }

  const renderSkeleton = () => {
    if (focus.type === 'portfolio' || focus.type === 'ungrouped') {
      return (
        <SkeletonTable aria-busy='true' aria-label='Loading'>
          <SkeletonControls>
            <SkeletonSearch />
            <SkeletonLine $w='120px' />
          </SkeletonControls>
          <SkeletonRows>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i} style={{ animationDelay: `${i * 60}ms` }}>
                <SkeletonLine $w='22%' />
                <SkeletonLine $w='12%' />
                <SkeletonLine $w='12%' />
                <SkeletonLine $w='12%' />
                <SkeletonPill />
              </SkeletonRow>
            ))}
          </SkeletonRows>
        </SkeletonTable>
      )
    }
    return (
      <SkeletonGrid aria-busy='true' aria-label='Loading'>
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} style={{ animationDelay: `${i * 70}ms` }}>
            <SkeletonHead>
              <SkeletonChip />
              <SkeletonPill />
            </SkeletonHead>
            <SkeletonLine $w='70%' />
            <SkeletonLine $w='45%' />
          </SkeletonCard>
        ))}
      </SkeletonGrid>
    )
  }

  return (
    <PageWrapper>
      <HeaderSection>
        <HeaderText>
          <Eyebrow>Estate overview</Eyebrow>
          <StyledPageHeader>Your Passports</StyledPageHeader>
          <PageDescription>
            Everything you look after, with what needs your attention first.
          </PageDescription>
        </HeaderText>
        <HeaderActions>
          <GhostButton type='button' data-tour-id='portfolios.new-estate' onClick={() => setShowCreateEstate(true)}>
            <AddRoundedIcon style={{ fontSize: 18 }} />
            New estate
          </GhostButton>
          <CreateBpButton type='button' onClick={() => navigate(PRIVATE_PATHS.createBp)}>
            <AddRoundedIcon style={{ fontSize: 18 }} />
            Create Building Passport
          </CreateBpButton>
        </HeaderActions>
      </HeaderSection>

      <Body>
        {isTop ? (
          <>
            {showSearch && (
              <JumpBar>
                <SearchRoundedIcon style={{ fontSize: 20 }} />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={altitude === 'estate' ? 'Search estates by name…' : 'Search portfolios by name…'}
                  aria-label='Search'
                />
                {query && (
                  <ClearBtn type='button' onClick={() => setQuery('')}>Clear</ClearBtn>
                )}
              </JumpBar>
            )}

            <MapSection>
              <PassportsMap points={mapPoints} onOpenBp={id => navigate(`/bp-list/${id}`)} showCompliance={showCompliance} loading={isLoading && !summary} />
            </MapSection>

            {showCompliance && summary && <AttentionBand items={attentionItems} />}

            <BrowseHead>
              <h2>Browse your estate</h2>
              {showingText && <Showing>{showingText}</Showing>}
            </BrowseHead>
            <TabRow>
              <LevelTabs counts={counts} active={altitude} onSelect={handleAltitude} loading={isLoading && !summary} />
            </TabRow>
          </>
        ) : (
          <>
            <FocusBreadcrumb trail={trail} onCrumb={handleCrumb} />
            {focusedContainer && (
              <ContainerHeader>
                <ContainerInfo>
                  {showCompliance && focusedContainer.score !== null && (
                    <ContainerScore
                      $colour={scoreColour(focusedContainer.score)}
                      title={`Compliance score ${formatPercent(focusedContainer.score)} — the share of weighted obligations satisfied across this group.`}
                      aria-label={`Compliance score ${formatPercent(focusedContainer.score)} — the share of weighted obligations satisfied across this group.`}
                    >
                      {formatPercent(focusedContainer.score)}
                    </ContainerScore>
                  )}
                  <ContainerText>
                    <ContainerName>{focusedContainer.name}</ContainerName>
                    {focusedContainer.childSummary && <ContainerSub>{focusedContainer.childSummary}</ContainerSub>}
                  </ContainerText>
                </ContainerInfo>
                {focusedContainer.detailPath && (
                  <DetailLink to={focusedContainer.detailPath}>
                    Open full detail →
                  </DetailLink>
                )}
              </ContainerHeader>
            )}
          </>
        )}

        {isLoading ? renderSkeleton() : (
          <GridArea>{renderGrid()}</GridArea>
        )}
      </Body>

      <CreatePortfolioModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreatePortfolio}
      />

      <EstateEditorModal
        open={showCreateEstate}
        onClose={() => { setShowCreateEstate(false); setReload(r => r + 1) }}
      />
    </PageWrapper>
  )
}

export default PortfoliosArea

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  box-sizing: border-box;
  padding-bottom: 40px;
`

const Unavailable = styled.div`
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
  padding: 44px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`

const UnavailableIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: ${radius.md};
  background: ${surface.wash};
  border: 1px solid ${line.base};
  color: ${ink.mute};
`

const UnavailableTitle = styled.h3`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 800;
  color: ${ink.base};
`

const UnavailableText = styled.p`
  margin: 0;
  max-width: 460px;
  font-size: ${font.emphasis};
  line-height: 1.55;
  color: ${ink.soft};
`

const UnavailableBtn = styled.button`
  margin-top: 6px;
  font: inherit;
  padding: 11px 20px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${font.emphasis};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover { filter: brightness(1.06); }
`

const HeaderSection = styled.div`
  width: 100%;
  padding: 0 24px;
  margin-bottom: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 0 16px;
    margin-bottom: 16px;
    flex-direction: column;
    align-items: stretch;
  }
`

const HeaderText = styled.div`
  min-width: 0;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const CreateBpButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  padding: 11px 18px;
  border: none;
  border-radius: ${radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  white-space: nowrap;

  &:hover {
    filter: brightness(0.94);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const StyledPageHeader = styled(PageHeader)`
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  font-family: 'FuturaPT-Book', system-ui, sans-serif;
  font-size: 34px;
  font-weight: normal;
  letter-spacing: -0.8px;
  line-height: 1.05;

  @media (max-width: 768px) {
    font-size: 26px;
    letter-spacing: -0.5px;
  }
`

const PageDescription = styled.p`
  color: ${ink.soft};
  font-size: 15px;
  line-height: 1.5;
  max-width: 760px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.45;
  }
`

const Body = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

const Eyebrow = styled.div`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 8px;
`

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  padding: 11px 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.md};
  background: ${surface.card};
  color: ${ink.base};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const JumpBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
  padding: 0 14px;
  height: 52px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
  transition: ${transition};
  color: ${ink.mute};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.secondary}22`};
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: ${ink.base};
    outline: none;
  }
  input::placeholder { color: ${ink.mute}; }
`

const ClearBtn = styled.button`
  flex-shrink: 0;
  border: 1px solid ${line.base};
  background: ${surface.wash};
  color: ${ink.soft};
  font: inherit;
  font-size: ${font.small};
  font-weight: 700;
  padding: 5px 10px;
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: ${transition};
  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const BrowseHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 13px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    font-size: ${font.heading};
    font-weight: 800;
    letter-spacing: -0.2px;
    color: ${ink.base};
  }
`

const Showing = styled.span`
  margin-left: auto;
  font-size: ${font.body};
  color: ${ink.mute};
  font-variant-numeric: tabular-nums;
`

const TabRow = styled.div`
  margin-bottom: 18px;
`

const MapSection = styled.div`
  margin-bottom: 22px;
`

const ContainerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 16px 18px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.card};
  box-shadow: ${shadow.card};
`

const ContainerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`

const ContainerScore = styled.div`
  flex-shrink: 0;
  min-width: 52px;
  height: 44px;
  padding: 0 12px;
  border-radius: ${radius.sm};
  background: ${({ $colour }) => `${$colour}14`};
  border: 1px solid ${({ $colour }) => `${$colour}40`};
  color: ${({ $colour }) => $colour};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.heading};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: help;
`

const ContainerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const ContainerName = styled.div`
  font-family: 'FuturaPT-Book', system-ui, sans-serif;
  font-size: ${font.title};
  font-weight: normal;
  letter-spacing: -0.3px;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ContainerSub = styled.div`
  font-size: ${font.small};
  color: ${ink.mute};
`

const DetailLink = styled(Link)`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const GridArea = styled.div`
  width: 100%;

  & > div {
    width: 100%;
    max-width: 100%;
  }
`

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const skeletonIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 14px;
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.card};
  animation: ${skeletonIn} 0.4s ease both;
`

const SkeletonHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const shimmerBase = css`
  background: #e7e9ee;
  border-radius: 6px;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const SkeletonChip = styled.span`
  ${shimmerBase}
  width: 34px;
  height: 34px;
  border-radius: 9px;
`

const SkeletonPill = styled.span`
  ${shimmerBase}
  width: 48px;
  height: 20px;
  border-radius: 999px;
`

const SkeletonLine = styled.span`
  ${shimmerBase}
  height: 11px;
  width: ${({ $w }) => $w || '100%'};
`

const SkeletonTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const SkeletonControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const SkeletonSearch = styled.span`
  ${shimmerBase}
  height: 36px;
  border-radius: 999px;
  width: 280px;
  max-width: 60%;
`

const SkeletonRows = styled.div`
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.card};
  overflow: hidden;
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 24px;
  border-bottom: 1px solid ${line.base};
  animation: ${skeletonIn} 0.4s ease both;
  &:last-child { border-bottom: none; }
`
